import React, { useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState, ChartControls } from '../ui';
import { useCustomerPurchasePercentages, SectionProps } from '../../hooks/useCustomerAnalyticsSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors } from '../../utils/themeHelpers';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface PurchasePercentagesChartProps extends SectionProps {
  branchId: number;
  months?: number;
}

const PurchasePercentagesChart: React.FC<PurchasePercentagesChartProps> = ({
  branchId,
  months = 12,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: percentageData, isLoading, isError, error, refetch } = useCustomerPurchasePercentages(branchId, months);

  // Component metadata
  const componentId = 'purchase-percentages-chart';
  const componentTitle = 'درصد تعداد و مبلغ خرید مشتریان جدید و قدیمی';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3 lg:col-span-2">
        <div className="flex flex-col items-center justify-center h-[400px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری نمودار…"
          />
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding lg:col-span-2">
        <ErrorMessage 
          message="خطا در دریافت درصد خریدها"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!percentageData || percentageData.length === 0) {
    return (
      <div className="card-base card-padding lg:col-span-2">
        <EmptyState />
      </div>
    );
  }

  const chartColors = getChartColors(currentTheme);

  const chartData = {
    labels: percentageData.map(item => item.month),
    datasets: [
      {
        label: 'سهم تعداد خرید · مشتریان جدید',
        data: percentageData.map(item => item.newCustomersCountPercent),
        borderColor: chartColors[1],
        backgroundColor: chartColors[1],
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: chartColors[1],
        tension: 0.4,
        fill: false
      },
      {
        label: 'سهم تعداد خرید · مشتریان قدیمی',
        data: percentageData.map(item => item.oldCustomersCountPercent),
        borderColor: chartColors[0],
        backgroundColor: chartColors[0],
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: chartColors[0],
        tension: 0.4,
        fill: false
      },
      {
        label: 'سهم مبلغ خرید · مشتریان جدید',
        data: percentageData.map(item => item.newCustomersAmountPercent),
        borderColor: chartColors[1],
        backgroundColor: chartColors[1],
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 3,
        pointHoverRadius: 5,
        pointStyle: 'rectRot',
        pointBackgroundColor: chartColors[1],
        tension: 0.4,
        fill: false
      },
      {
        label: 'سهم مبلغ خرید · مشتریان قدیمی',
        data: percentageData.map(item => item.oldCustomersAmountPercent),
        borderColor: chartColors[0],
        backgroundColor: chartColors[0],
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 3,
        pointHoverRadius: 5,
        pointStyle: 'rectRot',
        pointBackgroundColor: chartColors[0],
        tension: 0.4,
        fill: false
      }
    ]
  };

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3 lg:col-span-2">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="chart"
          dashboardSource="customerAnalytics"
          onInfo={() => openGuide(CHART_GUIDES.purchasePercentagesChart)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'customerAnalytics',
            componentType: 'chart',
            description: 'تحلیل درصد تعداد و مبلغ خرید مشتریان جدید و قدیمی در طول زمان',
            props: { branchId, months, compact },
            gridLayout: {
              containerClass: 'lg:col-span-2'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
        />
      </div>
      <h3 className="title-card">
        درصد تعداد و مبلغ خرید مشتریان جدید و قدیمی
      </h3>

      <div style={{ height: 400, width: '100%', padding: '20px 0' }}>
        <Line
          data={chartData}
          options={{
            ...getChartJsOptions(currentTheme),
            // responsive: true,
            // maintainAspectRatio: false,
            interaction: {
              mode: 'index' as const,
              intersect: false
            },
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                mode: 'index' as const,
                intersect: false,
                callbacks: {
                  title: function(tooltipItems: any) {
                    return tooltipItems[0]?.label || '';
                  },
                  label: function(context: any) {
                    const label = context.dataset.label || '';
                    const value = context.parsed.y;
                    const separator = '\u00A0:\u00A0';
                    const faValue = Number(value).toLocaleString('fa-IR');
                    return `${label}${separator}${faValue}\u066A`;
                  }
                }
              }
            },
            scales: {
              ...getChartJsOptions(currentTheme).scales,
              
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                title: {
                  display: true,
                  text: 'سهم (٪)',
                  ...getChartJsOptions(currentTheme).scales?.y?.title
                },
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return `${Number(value).toLocaleString('fa-IR')}٪`;
                  }
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default PurchasePercentagesChart;